import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Loader2,
  Upload,
  FileText,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  User,
  Phone,
  Briefcase,
  GraduationCap,
  Trash2,
} from "lucide-react";

const STATUSES = ["Draft", "Offered", "Accepted", "Rejected", "Withdrawn", "No-show"];
const HIRE_TYPES = ["Intern", "Full-time", "Part-time", "Contract"];

const STEPS = [
  { key: "basic", label: "Basic", icon: User },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "offer", label: "Offer", icon: Briefcase },
  { key: "extras", label: "Education & Docs", icon: GraduationCap },
];

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

export const emptyCandidateForm = () => ({
  company_id: "company",
  code: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "Male",
  date_of_birth: "",
  email: "",
  personal_email: "",
  phone: "",
  pan: "",
  aadhaar: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
  intended_employment_type: "Full-time",
  department_id: "",
  designation_id: "",
  work_location: "",
  location_id: "",
  proposed_joining_date: "",
  annual_ctc: 0,
  stipend_note: "",
  status: "Draft",
  offer_date: "",
  notes: "",
  education: [],
  documents: [],
});

function Field({ label, required, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
        {label}
        {required ? <span className="text-rose-500 ml-0.5">*</span> : null}
      </span>
      {children}
      {hint ? <span className="block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

export default function CandidateFormModal({
  editing,
  companies = [],
  departments = [],
  designations = [],
  locations = [],
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(editing?.id);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => (
    isEdit
      ? {
          ...emptyCandidateForm(),
          ...editing,
          annual_ctc: editing.annual_ctc || 0,
          education: Array.isArray(editing.education) ? editing.education : [],
          documents: Array.isArray(editing.documents) ? editing.documents : [],
        }
      : emptyCandidateForm()
  ));
  const [codeAuto, setCodeAuto] = useState(!isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [err, setErr] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (isEdit || !codeAuto) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/candidates/next-code", {
          params: { company_id: form.company_id || "company" },
        });
        if (!cancelled && r.data?.code) set("code", r.data.code);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, codeAuto, form.company_id]);

  const desigsForDept = useMemo(
    () => designations.filter((d) => !form.department_id || d.department_id === form.department_id),
    [designations, form.department_id],
  );

  const validateStep = (idx) => {
    if (idx === 0) {
      if (!form.first_name.trim()) return "First name is required";
      if (!form.gender) return "Gender is required";
      const pan = (form.pan || "").replace(/\s+/g, "").toUpperCase();
      const aadhaar = (form.aadhaar || "").replace(/\D+/g, "");
      if (!pan && !aadhaar) return "Provide PAN or Aadhaar (or both) for identity";
      if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return "Invalid PAN format (expected ABCDE1234F)";
      if (aadhaar && !/^\d{12}$/.test(aadhaar)) return "Invalid Aadhaar format (expected 12 digits)";
    }
    if (idx === 1) {
      if (!form.personal_email.trim()) return "Personal email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.personal_email.trim())) return "Enter a valid personal email";
      if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        return "Enter a valid official email, or leave it blank until hire";
      }
      const phoneDigits = (form.phone || "").replace(/\D+/g, "");
      if (!form.phone.trim()) return "Phone number is required";
      if (phoneDigits.length < 10) return "Phone number must have at least 10 digits";
    }
    return "";
  };

  const next = () => {
    const msg = validateStep(step);
    if (msg) {
      setErr(msg);
      return;
    }
    setErr("");
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const prev = () => {
    setErr("");
    setStep((s) => Math.max(0, s - 1));
  };

  const uploadFilesForCandidate = async (candidateId, files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name?.toLowerCase().includes("resume") || file.name?.toLowerCase().includes("cv")
        ? "Resume / CV"
        : (file.name || "Document"));
      fd.append("company_id", form.company_id || "company");
      await api.post(`/candidates/${candidateId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const saveForm = async () => {
    for (let i = 0; i <= step; i += 1) {
      const msg = validateStep(i);
      if (msg) {
        setErr(msg);
        setStep(i);
        return;
      }
    }
    // Also validate required contact if user jumped via stepper on edit
    const emailErr = validateStep(1);
    if (emailErr) {
      setErr(emailErr);
      setStep(1);
      return;
    }

    setSaving(true);
    setErr("");
    try {
      const payload = {
        ...form,
        pan: (form.pan || "").replace(/\s+/g, "").toUpperCase(),
        aadhaar: (form.aadhaar || "").replace(/\D+/g, ""),
        personal_email: (form.personal_email || "").trim().toLowerCase(),
        email: (form.email || "").trim().toLowerCase(),
        phone: (form.phone || "").trim(),
        annual_ctc: Number(form.annual_ctc || 0),
        department_id: form.department_id || null,
        designation_id: form.designation_id || null,
        location_id: form.location_id || null,
        date_of_birth: form.date_of_birth || null,
        proposed_joining_date: form.proposed_joining_date || null,
        offer_date: form.offer_date || null,
        education: (form.education || []).filter((r) =>
          (r.qualification || r.institution || r.year || r.specialization || "").toString().trim(),
        ),
      };
      delete payload.documents;
      delete payload.salutation;

      let saved;
      if (isEdit) {
        const { company_id, code, ...upd } = payload;
        const r = await api.put(`/candidates/${editing.id}`, upd);
        saved = r.data;
      } else {
        if (codeAuto) delete payload.code;
        const r = await api.post("/candidates", payload);
        saved = r.data;
      }

      if (pendingFiles.length && saved?.id) {
        try {
          await uploadFilesForCandidate(saved.id, pendingFiles);
          const refreshed = await api.get(`/candidates/${saved.id}`);
          saved = refreshed.data;
        } catch {
          // Candidate saved; warn on doc failure
          setErr("Candidate saved, but one or more documents failed to upload.");
        }
        setPendingFiles([]);
      }

      onSaved?.(saved);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadResumeNow = async (file) => {
    if (!isEdit || !file) return;
    setUploadingDoc(true);
    setErr("");
    try {
      await uploadFilesForCandidate(editing.id, [file]);
      const r = await api.get(`/candidates/${editing.id}`);
      setForm((f) => ({ ...f, documents: r.data.documents || [] }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDoc(false);
    }
  };

  const addEducationRow = () => {
    setForm((f) => ({
      ...f,
      education: [...(f.education || []), { qualification: "", institution: "", year: "", specialization: "" }],
    }));
  };

  const updateEducation = (idx, key, val) => {
    setForm((f) => {
      const edu = [...(f.education || [])];
      edu[idx] = { ...edu[idx], [key]: val };
      return { ...f, education: edu };
    });
  };

  const removeEducation = (idx) => {
    setForm((f) => ({ ...f, education: (f.education || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" data-testid="candidate-form-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {isEdit ? "Edit Candidate" : "Add Candidate"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Step {step + 1} of {STEPS.length} · {STEPS[step].label}
              {!isEdit ? " · CND code only — EMP/INT assigned at Hire" : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded" data-testid="candidate-form-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < step;
              const current = idx === step;
              return (
                <div key={s.key} className="flex items-center flex-1 last:flex-initial">
                  <button
                    type="button"
                    data-testid={`candidate-step-${s.key}`}
                    onClick={() => {
                      if (idx <= step) {
                        setErr("");
                        setStep(idx);
                      }
                    }}
                    className={`flex flex-col items-center text-center min-w-[72px] ${
                      idx <= step ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center transition border-2 ${
                        current
                          ? "bg-[#226DB4] border-[#226DB4] text-white"
                          : done
                            ? "bg-emerald-500 border-emerald-500 text-white"
                            : "bg-white border-slate-200 text-slate-400"
                      }`}
                    >
                      {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    <div className={`text-[11px] mt-1.5 font-medium ${current ? "text-[#226DB4]" : done ? "text-emerald-600" : "text-slate-400"}`}>
                      {s.label}
                    </div>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-[2px] mx-2 mb-5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {err && (
            <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm" data-testid="candidate-form-error">
              {err}
            </div>
          )}

          {step === 0 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">
                Same core identity fields as Add Employee. Only what you need before an offer.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Candidate Code" hint="Auto-generated CND sequence">
                  <div className="flex gap-2">
                    <input
                      className={inputCls}
                      value={form.code}
                      disabled={isEdit}
                      onChange={(e) => { setCodeAuto(false); set("code", e.target.value); }}
                      data-testid="candidate-code"
                    />
                    {!isEdit && (
                      <button type="button" className="px-3 text-xs border border-slate-200 rounded hover:bg-slate-50 shrink-0" onClick={() => setCodeAuto(true)}>
                        Auto
                      </button>
                    )}
                  </div>
                </Field>
                <Field label="Company">
                  <select
                    className={inputCls}
                    value={form.company_id}
                    disabled={isEdit}
                    onChange={(e) => set("company_id", e.target.value)}
                  >
                    {(companies.length ? companies : [{ id: "company", name: "Default" }]).map((co) => (
                      <option key={co.id} value={co.id}>{co.name || co.id}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Pipeline Status">
                  <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="First Name" required>
                  <input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="candidate-first-name" />
                </Field>
                <Field label="Middle Name">
                  <input className={inputCls} value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} />
                </Field>
                <Field label="Last Name">
                  <input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Gender" required>
                  <select className={inputCls} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                    <option value="">Select…</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Date of Birth">
                  <input type="date" className={inputCls} value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} />
                </Field>
                <Field label="Employment Type" hint="Intended hire type for the offer">
                  <select className={inputCls} value={form.intended_employment_type} onChange={(e) => set("intended_employment_type", e.target.value)}>
                    {HIRE_TYPES.map((t) => (
                      <option key={t} value={t}>{t === "Full-time" ? "Full-time (Permanent)" : t}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
                <h4 className="text-sm font-semibold text-slate-700 mb-1">Identity</h4>
                <p className="text-xs text-slate-500 mb-4">PAN or Aadhaar (or both) helps avoid duplicate candidates / employees.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="PAN" hint="Format ABCDE1234F">
                    <input
                      className={`${inputCls} uppercase`}
                      value={form.pan}
                      onChange={(e) => set("pan", e.target.value.toUpperCase())}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      data-testid="candidate-pan"
                    />
                  </Field>
                  <Field label="Aadhaar" hint="12 digits">
                    <input
                      className={inputCls}
                      value={form.aadhaar}
                      onChange={(e) => set("aadhaar", e.target.value.replace(/\D/g, "").slice(0, 12))}
                      placeholder="XXXX XXXX XXXX"
                      inputMode="numeric"
                      data-testid="candidate-aadhaar"
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Personal Email" required hint="Primary contact before the candidate joins.">
                  <input
                    type="email"
                    className={inputCls}
                    value={form.personal_email}
                    onChange={(e) => set("personal_email", e.target.value)}
                    data-testid="candidate-personal-email"
                    placeholder="personal@email.com"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+91 9XXXXXXXXX"
                    data-testid="candidate-phone"
                  />
                </Field>
                <Field
                  label="Official / Login Email"
                  hint="Optional now. Required when you Hire (used for employee login)."
                >
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="candidate-email"
                    placeholder="name@company.com (optional until hire)"
                  />
                </Field>
              </div>

              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-4">Communication Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Field label="Address Line 1">
                      <input className={inputCls} value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Address Line 2">
                      <input className={inputCls} value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} />
                    </Field>
                  </div>
                  <Field label="City">
                    <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
                  </Field>
                  <Field label="State">
                    <input className={inputCls} value={form.state} onChange={(e) => set("state", e.target.value)} />
                  </Field>
                  <Field label="Country">
                    <input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} />
                  </Field>
                  <Field label="PIN Code">
                    <input className={inputCls} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">
                Prefills the offer letter and Hire screen — change later if needed.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Department">
                  <select
                    className={inputCls}
                    value={form.department_id || ""}
                    onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value, designation_id: "" }))}
                  >
                    <option value="">— Select —</option>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </Field>
                <Field label="Designation">
                  <select className={inputCls} value={form.designation_id || ""} onChange={(e) => set("designation_id", e.target.value)}>
                    <option value="">— Select —</option>
                    {desigsForDept.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                  </select>
                </Field>
                <Field label="Joining Date">
                  <input type="date" className={inputCls} value={form.proposed_joining_date || ""} onChange={(e) => set("proposed_joining_date", e.target.value)} />
                </Field>
                <Field label="Offer Date">
                  <input type="date" className={inputCls} value={form.offer_date || ""} onChange={(e) => set("offer_date", e.target.value)} />
                </Field>
                <Field label="Work Location">
                  <input className={inputCls} value={form.work_location} onChange={(e) => set("work_location", e.target.value)} placeholder="City / office name" />
                </Field>
                <Field label="Branch / Location">
                  <select className={inputCls} value={form.location_id || ""} onChange={(e) => set("location_id", e.target.value)}>
                    <option value="">— None —</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name || l.code || l.id}</option>)}
                  </select>
                </Field>
                <Field label={form.intended_employment_type === "Intern" ? "Stipend (₹ / year)" : "Annual CTC (₹)"}>
                  <input type="number" min="0" className={inputCls} value={form.annual_ctc} onChange={(e) => set("annual_ctc", e.target.value)} />
                </Field>
                {form.intended_employment_type === "Intern" && (
                  <Field label="Stipend Note">
                    <input className={inputCls} value={form.stipend_note} onChange={(e) => set("stipend_note", e.target.value)} placeholder="Optional" />
                  </Field>
                )}
              </div>
              <Field label="Notes">
                <textarea className={inputCls} rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Internal HR notes (not printed on letters)" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700">Education</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Optional — same fields as employee education.</p>
                  </div>
                  <button type="button" onClick={addEducationRow} className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 text-[#226DB4]">
                    + Add row
                  </button>
                </div>
                {(form.education || []).length === 0 ? (
                  <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg px-4 py-6 text-center">
                    No education rows yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(form.education || []).map((row, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border border-slate-100 rounded-lg p-3 bg-slate-50/50">
                        <Field label="Qualification">
                          <input className={inputCls} value={row.qualification || ""} onChange={(e) => updateEducation(idx, "qualification", e.target.value)} />
                        </Field>
                        <Field label="Institution">
                          <input className={inputCls} value={row.institution || ""} onChange={(e) => updateEducation(idx, "institution", e.target.value)} />
                        </Field>
                        <Field label="Year">
                          <input className={inputCls} value={row.year || ""} onChange={(e) => updateEducation(idx, "year", e.target.value)} />
                        </Field>
                        <button type="button" onClick={() => removeEducation(idx)} className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs text-rose-600 border border-rose-200 rounded hover:bg-rose-50 mb-0.5">
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-1">Documents</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Resume / CV and supporting files. Stored under the candidate folder until Hire.
                </p>

                <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <Upload className="h-4 w-4 text-[#226DB4]" />
                  {uploadingDoc ? "Uploading…" : isEdit ? "Upload file" : "Attach file (uploads on save)"}
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    disabled={uploadingDoc}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (isEdit) uploadResumeNow(f);
                      else setPendingFiles((list) => [...list, f]);
                      e.target.value = "";
                    }}
                  />
                </label>

                <ul className="mt-3 space-y-1.5">
                  {(form.documents || []).map((d) => (
                    <li key={d.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <FileText className="h-4 w-4 text-slate-400" />
                      {d.name || d.file_name}
                    </li>
                  ))}
                  {pendingFiles.map((f, i) => (
                    <li key={`p-${i}`} className="flex items-center justify-between gap-2 text-sm text-slate-700 bg-sky-50 border border-sky-100 rounded px-2 py-1.5">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-sky-500 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-[10px] text-sky-600 shrink-0">Pending</span>
                      </span>
                      <button
                        type="button"
                        className="text-xs text-rose-600 hover:underline shrink-0"
                        onClick={() => setPendingFiles((list) => list.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-[#f9fafb]">
          <button
            type="button"
            onClick={prev}
            disabled={step === 0}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm border border-slate-200 rounded bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <div className="text-xs text-slate-500">{step + 1} / {STEPS.length}</div>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="candidate-form-next"
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={saveForm}
              data-testid="candidate-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-70"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Candidate"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
